import { useCallback, useMemo, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { SampleDataOverlay } from "./DiscoveryHubSampleState";
import type { LeaderboardPayload } from "./DraftLeaderboardPanel";
import {
  SAMPLE_APPROVAL_REVIEW_NOTES,
  SAMPLE_LEADERBOARD_ROWS,
} from "./leaderboardSampleData";
import LeaderboardTable from "./LeaderboardTable";
import {
  sortLeaderboardRows,
  toggleLeaderboardSort,
  type LeaderboardSortState,
} from "./leaderboardTableUtils";
import LeaderboardTierKpiCards from "./LeaderboardTierKpiCards";
import Button from "../../Components/Button";
import { HubPageLoading } from "../../Components/EngagementEmptyPanel";
import Modal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import {
  approveLeaderboard,
  sendBackLeaderboard,
} from "../../Store/Reducers/SponsorEngagementSlice";
import {
  LB_ACTION_BAR_MSG_PENDING,
  LB_FILTER_LBL,
  LB_FILTERS,
  LB_PAGE_HEADER,
  LB_PAGE_SUB,
  LB_PAGE_TITLE,
  LB_PANEL,
  LB_QUARTER_DISPLAY,
  LB_QUARTER_VALUE,
  lbActionBarClass,
} from "../../Utils/Helper/discoveryHubUi";
import {
  LEADERBOARD_TIER_ORDER,
  SPONSOR_ENGAGEMENT_THEATERS,
  formatQuarterPeriodLabel,
  rowMatchesTheaterFilter,
} from "../../Utils/Helper/sponsorEngagementUi";
import { toast } from "../../Utils/Helper/toast";

type FilterState = {
  theater: string;
  region: string;
  tier: string;
  search: string;
};

type ApprovalPendingPanelProps = {
  onActionComplete?: () => void | Promise<void>;
  currentQuarterPeriod?: string;
  leaderboard: LeaderboardPayload | null;
  selectedPeriod: string;
  onRefreshLeaderboard: () => Promise<void>;
  loading?: boolean;
};

const ApprovalPendingPanel = ({
  onActionComplete,
  currentQuarterPeriod,
  leaderboard,
  selectedPeriod,
  onRefreshLeaderboard,
  loading = false,
}: ApprovalPendingPanelProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );

  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [sendBackComments, setSendBackComments] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [sort, setSort] = useState<LeaderboardSortState>({
    col: "rank",
    dir: "asc",
  });
  const [filters, setFilters] = useState<FilterState>({
    theater: "All",
    region: "All",
    tier: "All",
    search: "",
  });

  const rows = leaderboard?.rows ?? [];
  const pubState = leaderboard?.publication_state;
  const period =
    leaderboard?.period || selectedPeriod || currentQuarterPeriod || "";
  const isCurrentQuarter = period === currentQuarterPeriod;
  const isSample =
    !loading && (pubState !== "pending_approval" || rows.length === 0);
  const tableRows = isSample ? SAMPLE_LEADERBOARD_ROWS : rows;

  const regionOptions = useMemo(() => {
    const regions = new Set<string>();
    tableRows.forEach(r => {
      if (r.region) regions.add(r.region);
    });
    return ["All", ...Array.from(regions).sort()];
  }, [tableRows]);

  const filteredRows = useMemo(() => {
    const result = tableRows.filter(r => {
      if (!rowMatchesTheaterFilter(r.theater, filters.theater)) return false;
      if (filters.region !== "All" && r.region !== filters.region) return false;
      if (filters.tier !== "All" && r.tier !== filters.tier) return false;
      if (
        filters.search &&
        !(r.sponsor_name || "")
          .toLowerCase()
          .includes(filters.search.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
    return sortLeaderboardRows(result, sort);
  }, [tableRows, filters, sort]);

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const result = await dispatch(approveLeaderboard());
      if (approveLeaderboard.rejected.match(result)) {
        toast.error((result.payload as string) || "Approve failed");
        return;
      }
      toast.success("Leaderboard approved and published");
      setApproveOpen(false);
      await onRefreshLeaderboard();
      onActionComplete?.();
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Approve failed"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendBack = async () => {
    if (!sendBackComments.trim()) {
      toast.error("Comments are required to send back");
      return;
    }
    setActionLoading(true);
    try {
      const result = await dispatch(
        sendBackLeaderboard({ comments: sendBackComments.trim() })
      );
      if (sendBackLeaderboard.rejected.match(result)) {
        toast.error((result.payload as string) || "Send back failed");
        return;
      }
      toast.success("Leaderboard sent back to ESL");
      setSendBackOpen(false);
      setSendBackComments("");
      await onRefreshLeaderboard();
      onActionComplete?.();
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Send back failed"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const goToDrilldown = (sponsorId: string) => {
    const returnTo = encodeURIComponent(
      `${location.pathname}${location.search}`
    );
    navigate(
      `${withOrg("/sponsor-drilldown")}?sponsorId=${sponsorId}&returnTo=${returnTo}`
    );
  };

  const showActionBar =
    isCurrentQuarter && pubState === "pending_approval" && rows.length > 0;

  const renderFilters = () => (
    <div className={LB_FILTERS}>
      <span className={LB_FILTER_LBL}>Filter</span>
      <select
        value={filters.theater}
        onChange={e => setFilters(f => ({ ...f, theater: e.target.value }))}
      >
        <option value="All">All theaters</option>
        {SPONSOR_ENGAGEMENT_THEATERS.map(t => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <select
        value={filters.region}
        onChange={e => setFilters(f => ({ ...f, region: e.target.value }))}
      >
        {regionOptions.map(r => (
          <option key={r} value={r}>
            {r === "All" ? "All regions" : r}
          </option>
        ))}
      </select>
      <select
        value={filters.tier}
        onChange={e => setFilters(f => ({ ...f, tier: e.target.value }))}
      >
        <option value="All">All tiers</option>
        {LEADERBOARD_TIER_ORDER.map(t => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <input
        placeholder="Search sponsor…"
        value={filters.search}
        onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
      />
      {period ? (
        <div className={LB_QUARTER_DISPLAY}>
          <span className={LB_FILTER_LBL}>Quarter</span>
          <span className={LB_QUARTER_VALUE}>
            {formatQuarterPeriodLabel(period)}
          </span>
        </div>
      ) : null}
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return <HubPageLoading message="Loading leaderboard…" />;
    }

    if (isSample) {
      return (
        <SampleDataOverlay message="Preview of approval pending — a submitted draft appears here when ESL selects you as approver.">
          <div className={lbActionBarClass(true)}>
            <p className={LB_ACTION_BAR_MSG_PENDING}>
              <strong>Pending your approval.</strong> Review the draft. Approve
              to publish, or send back with comments. ESL review notes:{" "}
              <em>&quot;{SAMPLE_APPROVAL_REVIEW_NOTES}&quot;</em>
            </p>
            <Button variant="secondary" disabled>
              Send Back…
            </Button>
            <Button variant="primary" disabled>
              Approve and Publish
            </Button>
          </div>
          <LeaderboardTierKpiCards rows={SAMPLE_LEADERBOARD_ROWS} />
          {renderFilters()}
          <LeaderboardTable
            rows={filteredRows}
            sortable
            sort={sort}
            onSortToggle={col =>
              setSort(prev => toggleLeaderboardSort(prev, col))
            }
          />
        </SampleDataOverlay>
      );
    }

    return (
      <>
        <LeaderboardTierKpiCards rows={rows} />
        {renderFilters()}
        <LeaderboardTable
          rows={filteredRows}
          sortable
          sort={sort}
          onSortToggle={col =>
            setSort(prev => toggleLeaderboardSort(prev, col))
          }
          onRowClick={row => row.sponsor_id && goToDrilldown(row.sponsor_id)}
        />
      </>
    );
  };

  return (
    <div className={`${LB_PANEL} approval-pending-panel`}>
      <div className={LB_PAGE_HEADER}>
        <h2 className={LB_PAGE_TITLE}>Approval Pending</h2>
        <p className={LB_PAGE_SUB}>
          {isSample ? SAMPLE_LEADERBOARD_ROWS.length : rows.length} sponsors ·
          review submitted draft before publishing
        </p>
      </div>

      {showActionBar ? (
        <div className={lbActionBarClass(true)}>
          <p className={LB_ACTION_BAR_MSG_PENDING}>
            <strong>Pending your approval.</strong> Review the draft. Approve to
            publish, or send back with comments.
            {leaderboard?.review_notes ? (
              <>
                {" "}
                ESL review notes:{" "}
                <em>&quot;{leaderboard.review_notes}&quot;</em>
              </>
            ) : null}
          </p>
          <Button variant="secondary" onClick={() => setSendBackOpen(true)}>
            Send Back…
          </Button>
          <Button variant="primary" onClick={() => setApproveOpen(true)}>
            Approve and Publish
          </Button>
        </div>
      ) : null}

      {renderBody()}

      <Modal
        isOpen={sendBackOpen}
        toggle={() => setSendBackOpen(false)}
        title="Send Leaderboard Back to ESL"
        subtitle="Comments are required. ESL will see your notes and can revise the draft."
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSendBackOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSendBack}
              loading={actionLoading}
              disabled={!sendBackComments.trim()}
            >
              Send Back
            </Button>
          </>
        }
      >
        <textarea
          className="w-full min-h-[100px] border rounded-lg p-3 text-sm"
          placeholder="What should ESL revise before resubmitting?"
          value={sendBackComments}
          onChange={e => setSendBackComments(e.target.value)}
        />
      </Modal>

      <Modal
        isOpen={approveOpen}
        toggle={() => setApproveOpen(false)}
        title="Approve and Publish Leaderboard"
        subtitle="This creates an immutable published snapshot visible to all stakeholders."
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setApproveOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleApprove}
              loading={actionLoading}
            >
              Approve and Publish
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 mb-0">
          You are publishing the <strong>{period}</strong> sponsor engagement
          leaderboard with <strong>{rows.length}</strong> ranked sponsors. This
          action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default ApprovalPendingPanel;
